import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@hero/web-icons';
import { useOrderTimeSlots } from '@hero/hooks-shared';
import { Calendar } from '../../../ui/Calendar/Calendar';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../ui/Sheet/Sheet';
import { Button } from '../../../ui/Button/Button';
import { TimeSlotChunks } from '../../../app/TimeSlotChunks/TimeSlotChunks';

export interface PickupDateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Fires every time the user picks a different date or time slot — there
   * is no "Save" step. Receives the materialised `from/to` `Date` range
   * expected by the order API along with the picked slot value.
   */
  onChange: (data: { date: Date; timeSlot: string; dateRange: { from: Date; to: Date } }) => void;
  /**
   * Optional seed range (e.g. an already-picked pickup date) so the
   * drawer re-opens with the user's last choice rather than auto-picking.
   */
  initialDateRange?: { from: Date; to: Date } | null;
}

/**
 * Drawer counterpart of `PickupDateModal`: same `useOrderTimeSlots`
 * logic, same calendar widget for date selection, with time slots
 * rendered as period-grouped chunks via `TimeSlotChunks` (Morning /
 * Afternoon / Evening / Night), matching the customer-app picker.
 *
 * No Save/Cancel commit step — every selection writes through to the
 * caller via `onChange`, the side-panel summary updates live, and the
 * footer "Done" button is purely a convenience to close.
 */
export function PickupDateDrawer({
  isOpen,
  onClose,
  onChange,
  initialDateRange,
}: PickupDateDrawerProps) {
  const { t } = useTranslation(['m-order', 'c-dates', 'c-main']);

  const {
    availableSlots,
    selectedSlot,
    selectedDate: dayjsSelectedDate,
    dateRange,
    selectSlot,
    selectDate,
    isDateValid,
  } = useOrderTimeSlots({ dateRange: initialDateRange ?? undefined });

  const selectedDate = dayjsSelectedDate.toDate();
  const isDateDisabled = (date: Date) => !isDateValid(date);

  // Fire `onChange` whenever the resolved date+slot moves. Tracking the
  // last emitted slot value avoids re-emitting on parent-driven prop
  // changes that come back through `initialDateRange`.
  const lastEmittedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedSlot || !dateRange) return;
    const key = `${selectedSlot}@${dateRange.from.getTime()}`;
    if (lastEmittedRef.current === key) return;
    lastEmittedRef.current = key;
    onChange({ date: selectedDate, timeSlot: selectedSlot, dateRange });
    // selectedDate / onChange are stable enough — the slot+range pair is
    // what truly drives the emission.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlot, dateRange?.from?.getTime(), dateRange?.to?.getTime()]);

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex flex-col p-0 bg-white w-[480px] sm:max-w-[480px]">
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-start gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center rounded-full bg-gray-100 size-8 hover:bg-gray-200 flex-shrink-0">
              <ArrowLeftIcon className="size-4 rtl:rotate-180" />
            </button>
            <div className="min-w-0 flex-1">
              <SheetTitle>{t('m-order:pickup_date.title')}</SheetTitle>
              <SheetDescription>
                {t('m-order:pickup_date.description', {
                  defaultValue: 'Pick a date and time slot for pickup.',
                })}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
              {t('c-dates:order_dates.pickup_date', { defaultValue: 'Pickup date' })}
            </div>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={date => {
                  if (date) selectDate(date);
                }}
                disabled={isDateDisabled}
                className="rounded-xl border border-gray-200"
              />
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-3">
              {t('m-order:drawer.select_pickup_time', { defaultValue: 'Pick up time' })}
            </div>
            <TimeSlotChunks
              slots={availableSlots}
              value={selectedSlot}
              onChange={selectSlot}
            />
          </div>
        </div>

        <div className="border-t p-4 flex items-center justify-end">
          <Button onClick={onClose}>{t('c-main:done', { defaultValue: 'Done' })}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
